import _isEqual from 'lodash/isEqual';
import { DateTime, Interval } from 'luxon';

import type { Business } from 'app-components/flux/business/businessTypes';
import type { UsageFilterItemProps } from 'app-components/flux/filters/filtersTypes';
import type {
  ChartType,
  DisplayOptionsType,
  ReportConfiguration,
  ReportConfigurationProps,
  ReportSubType,
  ReportType,
  RowType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { mkReportConfigFilters } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import {
  type CUSTOM_RANGE_VALUE,
  constructDynamicInterval,
  constructPredefinedInterval,
  type DynamicRange,
  type DynamicRangeIntervalTimestep,
  type GeneratedReportRanges,
  isGeneratedReportRangeTypeDynamic,
  type PREDEFINED_RANGE_VALUE,
  type Timestep,
} from 'app-components/utils/date';

import {
  clearConflictingRangeDateFieldsAll,
  mkDefaultComparisonRange,
  resolveComparisonPrimaryRangeDates,
} from './comparisonRanges';

export type ReportsSelectOption = {
  id: string;
  label: string;
  value: string;
  subLabel?: string;
  divider?: boolean;
  labelIcon?: React.ReactNode;
  disabled?: boolean;
};

// Type aliases for common return patterns
export type WithDefault<T> = [T, T];
export type OptionsWithDefault<T> = [T[], T];

// Semantic type aliases for interface properties - simplified without nulls
export type ReportOptions<T> = T[];

export type AdjustedmentProps = { changedField: keyof ReportConfiguration; currentDate?: DateTime };
type ReportConfigOrFilter = ReportConfiguration | ReportConfigurationProps['filter'] | null | undefined;

// Constants extracted from function
const GROUP_BY_OPTIONS = {
  account: { label: 'Account', value: 'ACCOUNT', id: 'group_by_account' },
  business: { label: 'Business', value: 'BUSINESS', id: 'group_by_business' },
  category: { label: 'Category', value: 'CATEGORY', id: 'group_by_category' },
  payee: { label: 'Payee', value: 'PAYEE', id: 'group_by_payee' },
  tag: { label: 'Tag', value: 'TAG', id: 'group_by_tag' },
  taxLineItem: { label: 'Tax Line Item', value: 'TAX_LINE_ITEM', id: 'group_by_tax_line_item' },
  usage: { label: 'Business/Personal', value: 'USAGE', id: 'group_by_usage' },
} as const;

const SEGMENT_BY_OPTIONS = {
  account: { label: 'Account', value: 'ACCOUNT', id: 'segment_by_account' },
  payee: { label: 'Payee', value: 'PAYEE', id: 'segment_by_payee' },
  tag: { label: 'Tag', value: 'TAG', id: 'segment_by_tag' },
  time: { label: 'Time', value: 'TIME', id: 'segment_by_time' },
  usage: { label: 'Business/Personal', value: 'USAGE', id: 'segment_by_usage' },
  none: { label: 'None', value: 'NONE', id: 'segment_by_none' },
  clientsAndProjects: { label: 'Clients & Projects', value: 'CUSTOMERPROJECT', id: 'segment_by_clients_and_projects' },
  clients: { label: 'Clients', value: 'CUSTOMER', id: 'segment_by_client' },
  overTime: { label: 'Over time', value: 'TIME', id: 'segment_by_overtime' },
  total: { label: 'Total', value: 'NONE', id: 'segment_by_total' },
} as const;

const TIMESTEP_OPTIONS = [
  { label: 'Day', value: 'DAY', id: 'time_step_by_day' },
  { label: 'Week', value: 'WEEK', id: 'time_step_by_week' },
  { label: 'Month', value: 'MONTH', id: 'time_step_by_month' },
  { label: 'Quarter', value: 'QUARTER', id: 'time_step_by_quarter' },
  { label: 'Year', value: 'YEAR', id: 'time_step_by_year' },
] as const;

const NO_TIMESTEP_OPTION = { label: 'Total', value: 'NONE', id: 'time_step_none' } as const;

/**
 * Get timestep options for a given report subtype, regardless of current columnType.
 * Used for nested submenus where we need to show timestep options even when Time isn't selected.
 */
export function getTimestepOptionsForSubtype(
  reportSubType: ReportSubType,
  reportType?: ReportType,
): ReportsSelectOption[] {
  if (reportSubType === 'TAX_SUMMARY' || reportSubType === 'BALANCE_SHEET') {
    return [];
  }
  return reportType === 'COMPARISON' ? [NO_TIMESTEP_OPTION, ...TIMESTEP_OPTIONS] : [...TIMESTEP_OPTIONS];
}

/**
 * The most periods a comparison interval may produce. Measured on staging (2026-10-09): 53 weekly
 * periods took 3.6 s and 5.5 MB, and 365 daily periods took 8.3 s and 20 MB. About 120 keeps a
 * request near 4 s.
 */
export const MAX_COMPARISON_INTERVAL_PERIODS = 120;

const TIMESTEP_UNITS = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
} as const;

/** How many interval buckets an inclusive date range spans, or undefined when it can't be counted. */
export const countComparisonIntervalPeriods = (
  startOn: string,
  endOn: string,
  timestep: Timestep | undefined,
): number | undefined => {
  const unit = timestep ? TIMESTEP_UNITS[timestep as keyof typeof TIMESTEP_UNITS] : undefined;
  const interval = Interval.fromDateTimes(DateTime.fromISO(startOn), DateTime.fromISO(endOn).endOf('day'));
  if (!unit || !interval.isValid) return undefined;
  return interval.count(unit);
};

/**
 * Disables each comparison interval whose primary range would produce more than
 * {@link MAX_COMPARISON_INTERVAL_PERIODS} periods. The current selection stays selectable-looking
 * and still generates: only choosing it from the menu is blocked. A range that can't be resolved
 * (ALL_DATES) is not capped.
 */
const capComparisonTimestepOptions = (
  options: ReportsSelectOption[],
  reportConfiguration: ReportConfiguration,
  currentDate: DateTime,
): ReportsSelectOption[] => {
  const primary = resolveComparisonPrimaryRangeDates(reportConfiguration, currentDate);
  if (!primary) return options;
  return options.map((option) => {
    const periodCount = countComparisonIntervalPeriods(primary.startOn, primary.endOn, option.value as Timestep);
    if (periodCount == null || periodCount <= MAX_COMPARISON_INTERVAL_PERIODS) return option;
    return { ...option, disabled: true, subLabel: 'Date range too long' };
  });
};

const PERSONAL_USAGE_OPTION: ReportsSelectOption = {
  id: 'PERSONAL',
  label: 'Personal',
  value: '0',
  labelIcon: undefined,
};

// COMPARISON is deliberately absent. Comparison is not a report *type* the user picks; it is entered
// and left through the Compare control alone — see `showComparison` and the ComparisonPicker. Adding
// an option here would give the conversion a second route with different semantics: the picker
// writes a range along with the type, while a bare type selection would leave the seeding to
// `adjustReportConfiguration`.
const REPORT_TYPE_OPTIONS = {
  summary: {
    label: 'Summary',
    value: 'SUMMARY',
    id: 'report_type_summary',
    subLabel: 'Totals grouped into rows and columns',
  },
  transaction: {
    label: 'Transaction',
    value: 'TRANSACTION',
    id: 'report_type_transaction',
    subLabel: 'Transactions organized by groups',
  },
} as const;

const REPORT_SUBTYPE_OPTIONS = {
  balanceSheet: { label: 'Balance Sheet', value: 'BALANCE_SHEET', id: 'report_sub_type_balance_sheet' },
  income: { label: 'Income', value: 'INCOME', id: 'report_sub_type_income' },
  netIncome: { label: 'Net Income', value: 'NET_INCOME', id: 'report_sub_type_net_income' },
  spending: { label: 'Spending', value: 'SPENDING', id: 'report_sub_type_spending' },
  netWorth: { label: 'Net Worth', value: 'NET_WORTH', id: 'report_sub_type_net_worth' },
  savings: { label: 'Savings', value: 'SAVINGS', id: 'report_sub_type_savings' },
  taxSummary: { label: 'Tax Summary', value: 'TAX_SUMMARY', id: 'report_sub_type_tax_summary' },
} as const;

const VIEW_TYPE_OPTIONS: ReportsSelectOption[] = [
  {
    id: 'DETAIL',
    label: 'Transaction',
    value: 'TRANSACTION',
    subLabel: 'Deductible transactions organized by tax line item',
    divider: true,
  },
  {
    id: 'SUMMARY',
    label: 'Tax schedule',
    value: 'SUMMARY',
    subLabel: 'Totals summarized by tax line item. Supported tax schedules: C, E, F',
  },
];

const CHART_TYPE_OPTIONS: Record<ReportSubType, string[][]> = {
  CUSTOM: [
    ['BAR_GROUPED', 'BAR_STACKED'],
    ['PIE', 'BAR_SIDEWAYS'],
  ],
  INCOME: [
    ['BAR_GROUPED', 'BAR_STACKED'],
    ['PIE', 'BAR_SIDEWAYS'],
  ],
  NET_INCOME: [['NET_INCOME', 'SANKEY'], ['SANKEY']],
  NET_WORTH: [
    ['MULTILINE', 'AREA'],
    ['PIE', 'BAR_SIDEWAYS'],
  ],
  NONE: [[], []],
  SAVINGS: [
    ['MULTILINE', 'AREA'],
    ['PIE', 'BAR_SIDEWAYS'],
  ],
  SPENDING: [
    ['BAR_GROUPED', 'BAR_STACKED'],
    ['PIE', 'BAR_SIDEWAYS'],
  ],
  TAX_SCHEDULE: [[], []],
  TAX_SUMMARY: [[], []],
  BALANCES_OVER_TIME: [[], []],
  BALANCE_SHEET: [[], []],
  PERFORMANCE_OVER_TIME: [[], []],
};

/**
 * Charts a COMPARISON report can show.
 *
 * A map of its own rather than a third slot in `CHART_TYPE_OPTIONS`, whose `string[][]` is
 * positionally `[overTime, notOverTime]` — a comparison report is neither. Its `columnType` is
 * pinned to NONE by the temporary QCS-0400-8 workaround, so it would always read the notOverTime
 * slot and get the transaction-report chart list.
 *
 * Keys must stay in step with `validReportSubtypesByType.COMPARISON`. A subtype that is absent
 * resolves to `[]`, i.e. no chart — the safe default.
 *
 * With a timestep, only the root chart is persisted; period-detail charts are drill-down state.
 */
const COMPARISON_DETAIL_CHART_TYPES = ['BAR_GROUPED_HORIZONTAL', 'WATERFALL'] as const satisfies readonly ChartType[];
const COMPARISON_OVER_TIME_CHART_TYPES = ['MULTILINE', 'BAR_GROUPED'] as const satisfies readonly ChartType[];

const COMPARISON_CHART_TYPE_OPTIONS: Partial<Record<ReportSubType, ChartType[]>> = {
  CUSTOM: [...COMPARISON_DETAIL_CHART_TYPES],
  INCOME: [...COMPARISON_DETAIL_CHART_TYPES],
  NET_INCOME: [...COMPARISON_DETAIL_CHART_TYPES],
  SPENDING: [...COMPARISON_DETAIL_CHART_TYPES],
};

/** Whether a chart type represents a comparison report or one of its drill-down views. */
export const isComparisonChartType = (chartType: ChartType | undefined): boolean =>
  chartType != null &&
  [...COMPARISON_DETAIL_CHART_TYPES, ...COMPARISON_OVER_TIME_CHART_TYPES].some((type) => type === chartType);

/** Saved chart choices depend on timestep; drill-down charts use the total comparison choices. */
export const getComparisonChartTypeOptions = (subtype: ReportSubType | undefined, timestep?: Timestep): ChartType[] => {
  const detailCharts = subtype ? COMPARISON_CHART_TYPE_OPTIONS[subtype] : undefined;
  if (!detailCharts) return [];
  return timestep != null && timestep !== 'NONE' ? [...COMPARISON_OVER_TIME_CHART_TYPES] : detailCharts;
};

/**
 * The chart a report entering COMPARISON should start on. Comparison reports used to be seeded with
 * `'NONE'` because they could render no chart at all; now they open on their chart by default, and
 * only a subtype with no comparison chart still gets NONE.
 */
const comparisonEntryChartType = (subtype: ReportSubType | undefined): ChartType =>
  getComparisonChartTypeOptions(subtype)[0] ?? 'NONE';

/**
 * The chart type to *render* for a config, as opposed to the one stored on it.
 *
 * Every comparison report saved before this feature carries `chartType: 'NONE'`, from the era when
 * comparison was grid-only. The heal in `adjustReportConfiguration` repairs those, but only when
 * something adjusts: opening a saved report dispatches `generateReport` with the config as-is, so
 * the heal never runs and the report that most needs the chart is the one that opens without it.
 *
 * Resolving at read time closes that without depending on an adjust pass, and without a write —
 * merely viewing a report should not rewrite it on the server. The stored value still heals on the
 * next real edit; this only decides what is drawn meanwhile.
 *
 * Non-comparison configs are returned untouched, so this is safe to call unconditionally.
 */
export const resolveDisplayChartType = (
  reportType: ReportType | undefined,
  reportSubType: ReportSubType | undefined,
  chartType: ChartType | undefined,
  timestep?: Timestep,
): ChartType | undefined => {
  if (reportType !== 'COMPARISON') return chartType;
  const allowed = getComparisonChartTypeOptions(reportSubType, timestep);
  if (chartType && allowed.includes(chartType)) return chartType;
  return allowed[0] ?? chartType;
};

// Utility functions
const isReportConfiguration = (configOrFilter: ReportConfigOrFilter): configOrFilter is ReportConfiguration =>
  Boolean(configOrFilter && 'filter' in configOrFilter);

export function extractUsageFromFilter(configOrFilter: ReportConfigOrFilter): string[] | undefined {
  const filter = isReportConfiguration(configOrFilter) ? configOrFilter.filter : configOrFilter;
  const usageFilterItem = filter?.filterItems?.find((item): item is UsageFilterItemProps => item.type === 'USAGE');
  return usageFilterItem?.usages?.map((u) => u.id);
}

function getGroupByOptions(currentSubtype: ReportSubType, segmentBy: string): OptionsWithDefault<ReportsSelectOption> {
  let groupByOptions: ReportOptions<ReportsSelectOption> = [];
  let defaultGroupByOption: ReportsSelectOption = GROUP_BY_OPTIONS.account;

  switch (currentSubtype) {
    case 'CUSTOM':
    case 'INCOME':
    case 'NET_INCOME':
    case 'SPENDING':
      groupByOptions = [
        GROUP_BY_OPTIONS.category,
        GROUP_BY_OPTIONS.account,
        GROUP_BY_OPTIONS.tag,
        GROUP_BY_OPTIONS.payee,
      ];
      defaultGroupByOption = GROUP_BY_OPTIONS.category;
      break;
    case 'SAVINGS':
      groupByOptions = [GROUP_BY_OPTIONS.account];
      defaultGroupByOption = GROUP_BY_OPTIONS.account;
      break;
    case 'TAX_SUMMARY':
      groupByOptions = [GROUP_BY_OPTIONS.business, GROUP_BY_OPTIONS.taxLineItem, GROUP_BY_OPTIONS.usage];
      defaultGroupByOption = GROUP_BY_OPTIONS.usage;
      break;
    case 'NET_WORTH':
      groupByOptions = [GROUP_BY_OPTIONS.account];
      defaultGroupByOption = GROUP_BY_OPTIONS.account;
      break;
    case 'BALANCE_SHEET':
      groupByOptions = [GROUP_BY_OPTIONS.business];
      defaultGroupByOption = GROUP_BY_OPTIONS.business;
      break;
    default:
      groupByOptions = [];
  }

  // Filter out current segmentBy value and NONE
  if (groupByOptions.length > 0) {
    groupByOptions = groupByOptions.filter((x) => x.value !== segmentBy && x.value !== 'NONE');
  }

  return [groupByOptions, defaultGroupByOption];
}

export function getSegmentByOptions(
  currentSubtype: ReportSubType,
  businessEnabled: boolean,
  reportConfiguration: ReportConfiguration,
): OptionsWithDefault<ReportsSelectOption> {
  const basicSegmentOptions: ReportsSelectOption[] = [
    SEGMENT_BY_OPTIONS.time,
    SEGMENT_BY_OPTIONS.account,
    SEGMENT_BY_OPTIONS.tag,
    SEGMENT_BY_OPTIONS.payee,
  ];
  let segmentByOptions: ReportOptions<ReportsSelectOption> = [];
  let defaultSegmentByOption: ReportsSelectOption = SEGMENT_BY_OPTIONS.time;

  // For any subtype with TRANSACTION reportType, don't allow segment by options
  if (reportConfiguration.reportType === 'TRANSACTION') {
    segmentByOptions = [];
    defaultSegmentByOption = SEGMENT_BY_OPTIONS.none;
  } else if (
    currentSubtype === 'TAX_SUMMARY' ||
    currentSubtype === 'TAX_SCHEDULE' ||
    currentSubtype === 'BALANCE_SHEET'
  ) {
    segmentByOptions = [];
    defaultSegmentByOption = SEGMENT_BY_OPTIONS.none;
  } else {
    const dropDownOptions = [...basicSegmentOptions];
    if (businessEnabled && currentSubtype === 'NET_INCOME') {
      dropDownOptions.push(SEGMENT_BY_OPTIONS.usage, SEGMENT_BY_OPTIONS.clients, SEGMENT_BY_OPTIONS.clientsAndProjects);
    }
    segmentByOptions = dropDownOptions.filter((x) => x.value !== reportConfiguration.rowType);
    defaultSegmentByOption =
      segmentByOptions.find((x) => x.value === reportConfiguration.columnType) || SEGMENT_BY_OPTIONS.time;
  }

  return [segmentByOptions, defaultSegmentByOption];
}

function getTimestepOptions(
  reportConfiguration: ReportConfiguration,
  currentSubtype: ReportSubType,
  advancedReporting: boolean,
  currentDate: DateTime = DateTime.local(),
): OptionsWithDefault<ReportsSelectOption> {
  let timestepOptions: ReportOptions<ReportsSelectOption> = [];
  let defaultTimestepOption: ReportsSelectOption =
    reportConfiguration.reportType === 'COMPARISON' ? NO_TIMESTEP_OPTION : TIMESTEP_OPTIONS[2]; // Month
  // COMPARISON always offers timestep options — see `showTimestep` in getReportFieldConfig.
  if (
    advancedReporting ||
    reportConfiguration.columnType === 'TIME' ||
    reportConfiguration.reportType === 'COMPARISON'
  ) {
    if (currentSubtype !== 'TAX_SUMMARY' && currentSubtype !== 'BALANCE_SHEET') {
      timestepOptions = getTimestepOptionsForSubtype(currentSubtype, reportConfiguration.reportType);
      if (reportConfiguration.reportType === 'COMPARISON') {
        timestepOptions = capComparisonTimestepOptions(timestepOptions, reportConfiguration, currentDate);
      }
      defaultTimestepOption =
        timestepOptions.find((x) => x.value === reportConfiguration.timestep) || defaultTimestepOption;
    }
  }

  return [timestepOptions, defaultTimestepOption];
}

function getUsageOptions(
  businessEnabled: boolean,
  usage: string[] | undefined,
  currentSubtype: ReportSubType,
  openBusinesses: Business[],
  reportTaxDetailPersonal: boolean,
  name?: string,
): WithDefault<ReportsSelectOption[]> {
  const personalSelection: ReportsSelectOption[] = [PERSONAL_USAGE_OPTION];

  // No usage selection for SAVINGS or NET_WORTH
  if (currentSubtype === 'SAVINGS' || currentSubtype === 'NET_WORTH') {
    return [[], []];
  }

  if (!businessEnabled) {
    return [personalSelection, personalSelection];
  }

  // QBP users
  const usageOptionsWithoutPersonal: ReportsSelectOption[] =
    openBusinesses
      ?.map((business, index) => ({
        id: business.id || 'unknown',
        label: business.name || 'Unknown',
        labelIcon: undefined,
        value: business.id || 'unknown',
        divider: index === (openBusinesses?.length || 0) - 1,
      }))
      .sort() || [];

  const shouldIncludePersonal =
    currentSubtype === 'NET_INCOME' ||
    (currentSubtype === 'TAX_SUMMARY' && reportTaxDetailPersonal) ||
    currentSubtype === 'SPENDING' ||
    currentSubtype === 'INCOME';

  let usageOptions: ReportsSelectOption[];
  if (shouldIncludePersonal) {
    usageOptions = [...usageOptionsWithoutPersonal, ...personalSelection];
  } else {
    usageOptions = usageOptionsWithoutPersonal;
  }

  const potentialValues: ReportsSelectOption[] = usageOptions;

  let defaultUsageOptions: ReportsSelectOption[];
  if (currentSubtype === 'TAX_SUMMARY') {
    // For TAX_SUMMARY on initial load, take first option
    defaultUsageOptions = potentialValues.slice(0, 1);
  } else if (currentSubtype === 'NET_INCOME') {
    // NOTE: This will change when open/save is implemented as we should NOT be keying on reportConfiguration.name
    if (name === 'profit-loss') {
      defaultUsageOptions = potentialValues.filter((item) => item.id !== 'PERSONAL');
    } else {
      defaultUsageOptions = [...personalSelection];
    }
  } else {
    // Default behavior for other subtypes on initial load
    defaultUsageOptions = potentialValues;
  }

  return [usageOptions, defaultUsageOptions];
}

function getReportTypeAndSubtypeOptions(
  reportConfiguration: ReportConfiguration,
  businessEnabled: boolean,
  reportType: ReportType,
  reportSubType: ReportSubType,
  advancedReporting: boolean,
): {
  reportTypeOptions: ReportOptions<ReportsSelectOption>;
  defaultReportTypeOption: ReportsSelectOption;
  reportSubTypeOptions: ReportOptions<ReportsSelectOption>;
  defaultReportSubTypeOption: ReportsSelectOption;
} {
  let reportTypeOptions: ReportOptions<ReportsSelectOption> = [];
  let defaultReportTypeOption: ReportsSelectOption = REPORT_TYPE_OPTIONS.summary;
  let reportSubTypeOptions: ReportOptions<ReportsSelectOption> = [];
  let defaultReportSubTypeOption: ReportsSelectOption = REPORT_SUBTYPE_OPTIONS.netIncome;

  // Show report type options for the INCOME and SPENDING subtypes, or for custom reports with
  // advanced reporting. Those are the only reports with two forms to choose between; Comparison is
  // not among the choices anywhere — see the note on REPORT_TYPE_OPTIONS.
  if (
    reportSubType === 'INCOME' ||
    reportSubType === 'SPENDING' ||
    (reportConfiguration.name === 'custom' && advancedReporting)
  ) {
    // Report type options
    reportTypeOptions = [REPORT_TYPE_OPTIONS.transaction, REPORT_TYPE_OPTIONS.summary];
    defaultReportTypeOption = REPORT_TYPE_OPTIONS.summary;
  }

  // Report subtype options only for custom reports with advanced reporting
  if (reportConfiguration.name === 'custom' && advancedReporting) {
    const subtypeOptions: ReportsSelectOption[] = [];

    if (reportType === 'SUMMARY') {
      if (businessEnabled) subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.balanceSheet);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.income);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.netIncome);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.spending);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.netWorth);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.savings);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.taxSummary);

      defaultReportSubTypeOption =
        subtypeOptions.find((x) => x.value === reportSubType) || REPORT_SUBTYPE_OPTIONS.netIncome;
    } else if (reportType === 'TRANSACTION') {
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.balanceSheet);
      subtypeOptions.push(REPORT_SUBTYPE_OPTIONS.taxSummary);

      defaultReportSubTypeOption =
        subtypeOptions.find((x) => x.value === reportSubType) || REPORT_SUBTYPE_OPTIONS.taxSummary;
    }

    reportSubTypeOptions = subtypeOptions;
  }

  return { reportTypeOptions, defaultReportTypeOption, reportSubTypeOptions, defaultReportSubTypeOption };
}

function getViewTypeAndChartTypeOptions(
  businessEnabled: boolean,
  currentSubtype: ReportSubType,
  reportType: ReportType,
  reportConfiguration: ReportConfiguration,
  _reportsGridGraph: boolean,
): {
  viewTypeOptions: ReportOptions<ReportsSelectOption>;
  defaultViewTypeOption: ReportsSelectOption;
  chartTypeOptions: string[];
} {
  let viewTypeOptions: ReportOptions<ReportsSelectOption> = [];
  let defaultViewTypeOption: ReportsSelectOption = VIEW_TYPE_OPTIONS[0];

  if (businessEnabled) {
    if (currentSubtype === 'TAX_SUMMARY') {
      viewTypeOptions = VIEW_TYPE_OPTIONS;

      // Extract usage from filter to check for personal usage (matches NewReports logic)
      const usage = extractUsageFromFilter(reportConfiguration);
      const hasPersonalUsage = usage?.includes('0'); // '0' is the ID for personal usage

      // Apply the same logic as NewReports: if TAX_SUMMARY with personal usage, force TRANSACTION type
      if (hasPersonalUsage) {
        defaultViewTypeOption = VIEW_TYPE_OPTIONS[0]; // TRANSACTION
      } else if (reportType === 'TRANSACTION') {
        defaultViewTypeOption = VIEW_TYPE_OPTIONS[0]; // TRANSACTION
      } else {
        defaultViewTypeOption = VIEW_TYPE_OPTIONS[1]; // SUMMARY
      }
    } else {
      viewTypeOptions = [];
    }
  }

  // Comparison reports get their own list, keyed by subtype and timestep. Deliberately not gated on the
  // comparison feature flag as well: a COMPARISON config can only exist while that flag is on, so a
  // second gate here would only be able to strand a report that already exists.
  if (reportType === 'COMPARISON') {
    return {
      viewTypeOptions,
      defaultViewTypeOption,
      chartTypeOptions: getComparisonChartTypeOptions(currentSubtype, reportConfiguration.timestep),
    };
  }

  const isOverTime = reportConfiguration.columnType === 'TIME';
  let chartTypeOptions: string[] = [];
  if (isOverTime) {
    chartTypeOptions = CHART_TYPE_OPTIONS[currentSubtype][0];
  } else {
    chartTypeOptions = CHART_TYPE_OPTIONS[currentSubtype][1];
  }

  return { viewTypeOptions, defaultViewTypeOption, chartTypeOptions };
}

export interface ReportFieldConfig {
  allowedSubtypes: Record<ReportSubType, boolean>;
  allowedGroupBy: Record<RowType, boolean>;
  allowsTimestep: boolean;
  allowsSegmentBy: boolean;
  defaultSubtype: ReportSubType;
  defaultGroupBy: RowType;
  defaultSegmentBy: RowType;
  defaultTimestep?: Timestep | 'NONE';

  // Control visibility/availability
  showViewType: boolean;
  showUsage: boolean;
  showDateRange: boolean;
  showBalanceAsOf: boolean;
  showGroupBy: boolean;
  showSegmentBy: boolean;
  showTimestep: boolean;
  showReportType: boolean;
  showReportSubType: boolean;
  showFilters: boolean;
  /** The comparison range picker; only meaningful once the report is already a comparison report. */
  showComparison: boolean;

  // Usage control behavior
  usageSingleSelect: boolean; // true for TAX_SUMMARY, false for others

  // Conditional disabling
  disableSegmentByForNetWorth: boolean;
  disableTimestepForNetWorth: boolean;

  // Available options for dropdowns
  reportTypeOptions: ReportOptions<ReportsSelectOption>;
  reportSubTypeOptions: ReportOptions<ReportsSelectOption>;
  groupByOptions: ReportOptions<ReportsSelectOption>;
  segmentByOptions: ReportOptions<ReportsSelectOption>;
  timestepOptions: ReportOptions<ReportsSelectOption>;
  usageOptions: ReportsSelectOption[];
  viewTypeOptions: ReportOptions<ReportsSelectOption>;
  chartTypeOptions: string[];

  // Default selected values
  defaultReportTypeOption: ReportsSelectOption;
  defaultReportSubTypeOption: ReportsSelectOption;
  defaultGroupByOption: ReportsSelectOption;
  defaultSegmentByOption: ReportsSelectOption;
  defaultTimestepOption: ReportsSelectOption;
  defaultUsageOptions: ReportsSelectOption[];
  defaultViewTypeOption: ReportsSelectOption;
}

// Configuration lookup tables
// We need separate configs for TAX_SUMMARY based on reportType and usage
const reportConfigKey = (
  reportType: ReportType,
  reportSubType: ReportSubType,
  usage?: string[],
): ReportSubType | 'TAX_SUMMARY_TRANSACTION' | 'TAX_SUMMARY_SUMMARY' | 'TAX_SCHEDULE_TRANSACTION' => {
  // For any tax report (TAX_SUMMARY or TAX_SCHEDULE), if personal usage is selected,
  // treat it as a tax transaction report
  const hasPersonalUsage = usage && usage.includes('0');

  if (reportSubType === 'TAX_SUMMARY') {
    if (reportType === 'TRANSACTION' || hasPersonalUsage) {
      return 'TAX_SUMMARY_TRANSACTION';
    } else {
      return 'TAX_SUMMARY_SUMMARY';
    }
  }

  if (reportSubType === 'TAX_SCHEDULE') {
    // For TAX_SCHEDULE with personal usage, treat it as TAX_SUMMARY_TRANSACTION
    if (hasPersonalUsage) {
      return 'TAX_SUMMARY_TRANSACTION';
    } else if (reportType === 'TRANSACTION') {
      return 'TAX_SCHEDULE_TRANSACTION';
    }
  }

  // TRANSACTION reports generally have different behavior
  if (reportType === 'TRANSACTION') {
    if (reportSubType === 'TAX_SCHEDULE') {
      return 'TAX_SCHEDULE_TRANSACTION';
    }
    // For other subtypes in TRANSACTION mode, use the base subtype
  }

  return reportSubType;
};

const reportTypeRules: Record<
  ReportType,
  {
    allowedSubtypes: Record<ReportSubType, boolean>;
    defaultSubtype: ReportSubType;
  }
> = {
  SUMMARY: {
    allowedSubtypes: {
      TAX_SUMMARY: true,
      INCOME: true,
      NET_INCOME: true,
      CUSTOM: true,
      BALANCE_SHEET: true,
      BALANCES_OVER_TIME: false,
      NET_WORTH: true,
      PERFORMANCE_OVER_TIME: false,
      SAVINGS: true,
      SPENDING: true,
      TAX_SCHEDULE: false,
      NONE: false,
    },
    defaultSubtype: 'NET_INCOME',
  },
  TRANSACTION: {
    allowedSubtypes: {
      TAX_SCHEDULE: true,
      TAX_SUMMARY: true,
      BALANCE_SHEET: false,
      BALANCES_OVER_TIME: false,
      CUSTOM: false,
      INCOME: true,
      NET_INCOME: false,
      NET_WORTH: false,
      PERFORMANCE_OVER_TIME: false,
      SAVINGS: false,
      SPENDING: true,
      NONE: false,
    },
    defaultSubtype: 'TAX_SCHEDULE',
  },
  COMPARISON: {
    // Transaction-derived subtypes only. Balance-based subtypes (NET_WORTH, SAVINGS,
    // BALANCE_SHEET) are excluded because comparison ranges compare flows over a period, and the
    // tax subtypes are excluded because `adjustReportConfiguration` force-rewrites any tax report
    // with personal usage to `reportType: 'TRANSACTION'`, which would silently convert the report.
    allowedSubtypes: {
      BALANCE_SHEET: false,
      BALANCES_OVER_TIME: false,
      CUSTOM: true,
      INCOME: true,
      NET_INCOME: true,
      NET_WORTH: false,
      PERFORMANCE_OVER_TIME: false,
      SAVINGS: false,
      SPENDING: true,
      TAX_SCHEDULE: false,
      TAX_SUMMARY: false,
      NONE: false,
    },
    defaultSubtype: 'NET_INCOME',
  },
};

const subtypeRules: Record<
  ReportSubType | 'TAX_SUMMARY_TRANSACTION' | 'TAX_SUMMARY_SUMMARY' | 'TAX_SCHEDULE_TRANSACTION',
  {
    allowedGroupBy: Record<RowType, boolean>;
    defaultGroupBy: RowType;
    allowsTimestep: boolean;
    allowsSegmentBy: boolean;
    defaultSegmentBy: RowType;
    defaultTimestep?: Timestep | 'NONE';
  }
> = {
  BALANCE_SHEET: {
    allowedGroupBy: {
      BUSINESS: true,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'BUSINESS',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  BALANCES_OVER_TIME: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'NONE',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  CUSTOM: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'CATEGORY',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  INCOME: {
    allowedGroupBy: {
      ACCOUNT: true,
      CATEGORY: true,
      PAYEE: true,
      TAG: true,
      BUSINESS: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'CATEGORY',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  NET_INCOME: {
    allowedGroupBy: {
      ACCOUNT: true,
      CATEGORY: true,
      PAYEE: true,
      TAG: true,
      BUSINESS: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'CATEGORY',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  NET_WORTH: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'ACCOUNT',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  PERFORMANCE_OVER_TIME: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'NONE',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  SAVINGS: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'ACCOUNT',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  SPENDING: {
    allowedGroupBy: {
      ACCOUNT: true,
      CATEGORY: true,
      PAYEE: true,
      TAG: true,
      BUSINESS: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'CATEGORY',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  TAX_SCHEDULE: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'USAGE',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  TAX_SUMMARY: {
    allowedGroupBy: {
      BUSINESS: true,
      TAX_LINE_ITEM: true,
      USAGE: true,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'BUSINESS',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'YEAR',
  },
  NONE: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'NONE',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'MONTH',
  },
  // Special configurations for TAX_SUMMARY based on context
  TAX_SUMMARY_TRANSACTION: {
    allowedGroupBy: {
      BUSINESS: true,
      TAX_LINE_ITEM: true,
      USAGE: true,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'USAGE',
    allowsTimestep: false,
    allowsSegmentBy: false,
    defaultSegmentBy: 'NONE',
    defaultTimestep: undefined,
  },
  TAX_SUMMARY_SUMMARY: {
    allowedGroupBy: {
      BUSINESS: true,
      TAX_LINE_ITEM: true,
      USAGE: true,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'BUSINESS',
    allowsTimestep: true,
    allowsSegmentBy: true,
    defaultSegmentBy: 'TIME',
    defaultTimestep: 'YEAR',
  },
  TAX_SCHEDULE_TRANSACTION: {
    allowedGroupBy: {
      BUSINESS: false,
      ACCOUNT: false,
      CATEGORY: false,
      PAYEE: false,
      TAG: false,
      USAGE: false,
      TAX_LINE_ITEM: false,
      NONE: false,
      TAX_SCHEDULE: false,
      CLIENT: false,
      PROJECT: false,
      TIME: false,
    },
    defaultGroupBy: 'USAGE',
    allowsTimestep: false,
    allowsSegmentBy: false,
    defaultSegmentBy: 'NONE',
    defaultTimestep: undefined,
  },
};

/**
 * Gets the field availability configuration for a given report type and subtype
 */
export function getReportFieldConfig(
  reportConfiguration: ReportConfiguration,
  businessEnabled: boolean,
  advancedReporting: boolean,
  reportTaxDetailBusiness: boolean,
  openBusinesses: Business[],
  reportTaxDetailPersonal: boolean,
  reportsGridGraph: boolean,
  // Defaulted so the existing positional callers — and the ~20 call sites in the unit tests — keep
  // compiling unchanged. Comparison is off unless a caller explicitly threads the release flag in.
  comparisonReports = false,
): ReportFieldConfig {
  const { reportType, reportSubType } = reportConfiguration;

  // Extract usage from filter.filterItems (usage comes from Filter, not reportConfiguration)
  const usage = extractUsageFromFilter(reportConfiguration);

  const typeRules = reportTypeRules[reportType];
  let currentSubtype: ReportSubType;
  if (reportSubType && typeRules.allowedSubtypes[reportSubType]) {
    currentSubtype = reportSubType;
  } else {
    currentSubtype = typeRules.defaultSubtype;
  }

  // Get the appropriate config key (handles TAX_SUMMARY special cases)
  const configKey = reportConfigKey(reportType, currentSubtype, usage);
  const subtypeRule = subtypeRules[configKey];

  // Get options using utility functions
  const [groupByOptions, defaultGroupByOption] = getGroupByOptions(currentSubtype, reportConfiguration.columnType);
  const currentGroupByOption =
    groupByOptions?.find((x) => x.value === reportConfiguration.rowType) || groupByOptions?.[0] || defaultGroupByOption;

  const [timestepOptions, defaultTimestepOption] = getTimestepOptions(
    reportConfiguration,
    currentSubtype,
    advancedReporting,
  );

  const [segmentByOptions, defaultSegmentByOption] = getSegmentByOptions(
    currentSubtype,
    businessEnabled,
    reportConfiguration,
  );

  const [usageOptions, defaultUsageOptions] = getUsageOptions(
    businessEnabled,
    usage,
    currentSubtype,
    openBusinesses,
    reportTaxDetailPersonal,
    reportConfiguration.name,
  );

  const { reportTypeOptions, defaultReportTypeOption, reportSubTypeOptions, defaultReportSubTypeOption } =
    getReportTypeAndSubtypeOptions(reportConfiguration, businessEnabled, reportType, reportSubType, advancedReporting);

  const { viewTypeOptions, defaultViewTypeOption, chartTypeOptions } = getViewTypeAndChartTypeOptions(
    businessEnabled,
    currentSubtype,
    reportType,
    reportConfiguration,
    reportsGridGraph,
  );

  // Context-dependent visibility logic - check both business rules AND if options exist
  const showViewType = Boolean(
    reportTaxDetailBusiness &&
      businessEnabled &&
      usage &&
      usage[0] !== '0' && // Check for personal usage ID '0' instead of 'PERSONAL'
      currentSubtype === 'TAX_SUMMARY' &&
      viewTypeOptions.length > 0,
  );

  const showUsage = Boolean(businessEnabled && usageOptions.length > 0);

  // GroupBy is hidden for certain subtypes (TAX_SUMMARY, BALANCE_SHEET), when rowType is NONE, or when no options exist
  const showGroupBy =
    currentSubtype !== 'TAX_SUMMARY' &&
    currentSubtype !== 'BALANCE_SHEET' &&
    reportConfiguration.rowType !== 'NONE' &&
    groupByOptions.length > 0;

  const showBalanceAsOf = currentSubtype === 'BALANCE_SHEET';
  const showDateRange = !showBalanceAsOf;

  const isNetWorthDisabled = currentSubtype === 'NET_WORTH' || currentSubtype === 'SAVINGS';
  // Allow timestep adjustment for NET_WORTH and SAVINGS since they are time-based account reports
  const isNetWorthTabDisabled = false;

  return {
    allowedSubtypes: typeRules.allowedSubtypes,
    allowedGroupBy: subtypeRule.allowedGroupBy,
    allowsTimestep: subtypeRule.allowsTimestep,
    allowsSegmentBy: subtypeRule.allowsSegmentBy,
    defaultSubtype: typeRules.defaultSubtype,
    defaultGroupBy: subtypeRule.defaultGroupBy,
    defaultSegmentBy: subtypeRule.defaultSegmentBy,
    defaultTimestep: subtypeRule.defaultTimestep,

    // Context-dependent visibility
    showViewType,
    showUsage,
    showDateRange,
    showBalanceAsOf,
    showGroupBy: Boolean(showGroupBy),
    // Show if options exist — except on a comparison report, where segment-by has nothing to
    // segment: its columns are generated from `comparisonRanges` x timestep, not from a second
    // dimension, and `columnType` is forced to a single value for the whole report. Offering the
    // control would present a choice that cannot affect the output.
    //
    // Gated here rather than by emptying `segmentByOptions`, because the options still feed
    // `adjustSegmentByIfInvalid` and the default-resolution below; this hides the control without
    // changing what a conversion back out of COMPARISON can restore.
    showSegmentBy: Boolean(segmentByOptions.length > 0 && reportConfiguration.reportType !== 'COMPARISON'),
    showTimestep:
      currentSubtype === 'NET_WORTH' ||
      currentSubtype === 'SAVINGS' ||
      // Comparison reports always expose timestep: it defines the Period groups that comparison
      // ranges are positionally aligned across, so it is meaningful independently of columnType.
      // `NONE` is a valid choice here — it collapses each range to a single Total group.
      reportConfiguration.reportType === 'COMPARISON' ||
      ((currentSubtype === 'NET_INCOME' || currentSubtype === 'SPENDING' || currentSubtype === 'INCOME') &&
        reportConfiguration.columnType === 'TIME'),
    showReportType: Boolean(
      // Hidden outright on a comparison report. Comparison is entered and left through the Compare
      // control alone, and the underlying subtype of a comparison report is usually INCOME or
      // SPENDING — so without this term the picker would appear on it offering Transaction and
      // Summary, converting the report out of comparison by a second, unlabelled route.
      reportConfiguration.reportType !== 'COMPARISON' &&
        (currentSubtype === 'INCOME' || currentSubtype === 'SPENDING') &&
        reportTypeOptions.length > 0,
    ),
    showReportSubType: Boolean(
      reportConfiguration.name === 'custom' && advancedReporting && reportSubTypeOptions.length > 0,
    ),
    showFilters: true, // Always show filters

    // The Compare control is an entry point, not just an editor: it appears on a report that has no
    // comparison yet, opens with nothing selected, and converting to COMPARISON is what Apply does.
    // So visibility keys on whether the current subtype *could* become a comparison report, taken
    // from the same table that governs the conversion so the two cannot drift.
    //
    // Two clauses, for different reasons:
    //   - the flag gates the eligible-but-not-yet-comparison case, because that surfaces an
    //     unreleased feature on ordinary Income and Spending reports;
    //   - an existing COMPARISON config shows the control regardless, since such a config can arrive
    //     by sync and hiding the control would leave its ranges uneditable and unclearable.
    showComparison: Boolean(
      reportConfiguration.reportType === 'COMPARISON' ||
        (comparisonReports && reportTypeRules.COMPARISON.allowedSubtypes[currentSubtype]),
    ),

    // Usage behavior
    usageSingleSelect: currentSubtype === 'TAX_SUMMARY',

    // Conditional disabling
    disableSegmentByForNetWorth: isNetWorthDisabled,
    disableTimestepForNetWorth: isNetWorthTabDisabled,

    reportTypeOptions,
    reportSubTypeOptions,
    groupByOptions,
    segmentByOptions,
    timestepOptions,
    usageOptions,
    viewTypeOptions,
    chartTypeOptions,

    // Default selected values
    defaultReportTypeOption,
    defaultReportSubTypeOption,
    defaultGroupByOption: currentGroupByOption,
    defaultSegmentByOption,
    defaultTimestepOption,
    defaultUsageOptions,
    defaultViewTypeOption,
  };
}

///////////////////////////////////////////////////////////////////////////////////////////////////
// REPORT CONFIGURATION ADJUSTMENTS
//
// This module now includes both:
// 1. Field availability logic (getReportFieldConfig) - determines what fields should be shown/hidden
// 2. Configuration adjustment logic - validates and fixes invalid field combinations
//
// The adjustment logic is based on NewReports/state.tsx but integrated here for better cohesion.
//
// Usage:
// - Use adjustReportConfiguration() to apply all adjustments
// - Individual adjustment functions are also available for specific scenarios
//
// The adjustment system ensures report configurations remain valid when fields change,
// handling complex interdependencies between reportType, reportSubType, groupBy, segmentBy, etc.
///////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Report Type Adjustments
 */
export const adjustReportTypeIfInvalid = (config: ReportConfiguration, reportType: ReportType): ReportConfiguration => {
  const reportSubType = config.reportSubType;
  const currentReportType = config.reportType;

  // Extract usage from filter to check for personal usage (ID '0')
  const usage = extractUsageFromFilter(config);

  if ((reportSubType === 'TAX_SUMMARY' || reportSubType === 'TAX_SCHEDULE') && usage?.includes('0')) {
    return config.merge({
      reportType: 'TRANSACTION',
      reportSubType: 'TAX_SUMMARY',
      rowType: 'USAGE',
      columnType: 'NONE',
      timestep: 'NONE',
    });
  } else {
    // Handle general TRANSACTION ↔ SUMMARY transitions
    if (reportType === 'SUMMARY' && currentReportType === 'TRANSACTION') {
      // Switching from TRANSACTION to SUMMARY
      return config.merge({
        reportType,
        columnType: 'TIME',
        timestep: 'MONTH',
        chartType: 'BAR_STACKED',
      });
    } else if (reportType === 'TRANSACTION' && currentReportType === 'SUMMARY') {
      // Switching from SUMMARY to TRANSACTION
      return config.merge({
        reportType,
        columnType: 'NONE',
        timestep: 'NONE',
        chartType: 'PIE',
      });
    } else if (reportType === 'SUMMARY' && currentReportType === 'SUMMARY') {
      // Already SUMMARY but check if columnType/timestep/chartType need adjustment for INCOME/SPENDING
      if (reportSubType === 'INCOME' || reportSubType === 'SPENDING') {
        const updates: Partial<ReportConfigurationProps> = {};
        if (config.columnType === 'NONE') updates.columnType = 'TIME';
        if (config.timestep === 'NONE') updates.timestep = 'MONTH';
        if (config.chartType !== 'BAR_STACKED') updates.chartType = 'BAR_STACKED';
        if (Object.keys(updates).length > 0) {
          return config.merge(updates);
        }
      }
      return config;
    } else if (reportType === 'TRANSACTION' && currentReportType === 'TRANSACTION') {
      // Already TRANSACTION but check if columnType/timestep/chartType need adjustment for INCOME/SPENDING
      if (reportSubType === 'INCOME' || reportSubType === 'SPENDING') {
        const updates: Partial<ReportConfigurationProps> = {};
        if (config.columnType !== 'NONE') updates.columnType = 'NONE';
        if (config.timestep !== 'NONE') updates.timestep = 'NONE';
        if (config.chartType !== 'PIE') updates.chartType = 'PIE';
        if (Object.keys(updates).length > 0) {
          return config.merge(updates);
        }
      }
      return config;
    } else if (reportType === 'COMPARISON' && currentReportType !== 'COMPARISON') {
      // Switching INTO COMPARISON from TRANSACTION or SUMMARY.
      //
      // Without this arm both transitions fell through to the bare `merge({ reportType })` below
      // and carried the source type's presentation fields across:
      //   - from TRANSACTION: `timestep: 'NONE'`, which leaves a comparison report with no Period
      //     groups at all — only the Total group — so the ranges have nothing to align across.
      //   - from either: a `chartType` (PIE / BAR_STACKED) that comparison reports do not render.
      //
      // An explicit timestep of NONE is still a valid choice once the user is in COMPARISON; this
      // only supplies a sensible starting point on entry. `adjustSegmentByIfInvalid` enforces
      // TIME columns for comparison reports after this transition.
      return config.merge({
        reportType,
        timestep: !config.timestep || config.timestep === 'NONE' ? 'MONTH' : config.timestep,
        chartType: comparisonEntryChartType(config.reportSubType),
      });
    } else {
      return config.merge({ reportType });
    }
  }
};

const reportSubtypesWithoutTransactions = new Set<ReportSubType>(['BALANCE_SHEET', 'NET_WORTH', 'SAVINGS']);

/**
 * Display option predicate: only show "Show excluded transactions" for reports
 * whose grid/register can surface transaction-backed rows.
 */
export const shouldShowExcludedTransactionsDisplayOption = (reportSubType?: ReportSubType): boolean =>
  reportSubType === undefined || !reportSubtypesWithoutTransactions.has(reportSubType);

/**
 * True when the report is filtered to transactions marked "Excluded in reports". Every row such a
 * filter returns is excluded, so hiding excluded transactions would always leave the report empty.
 */
export function isExcludedInReportsFilterApplied(configOrFilter: ReportConfigOrFilter): boolean {
  const filter = isReportConfiguration(configOrFilter) ? configOrFilter.filter : configOrFilter;
  return Boolean(filter?.filterItems?.some((item) => item.type === 'ADVANCED' && item.excludedInReports === true));
}

/**
 * The "Show excluded transactions" value the report should render with. The "Excluded in reports"
 * filter forces it on for as long as the filter is applied; this is derived rather than written to
 * the config, so the saved preference is untouched and comes back when the filter is removed.
 */
export const getEffectiveShowExcludedTxns = (
  config: Pick<ReportConfigurationProps, 'displayOptions' | 'filter'> | null | undefined,
): boolean => isExcludedInReportsFilterApplied(config?.filter) || Boolean(config?.displayOptions?.showExcludedTxns);

/** Apply the effective display option to a request without changing the saved configuration. */
export const getReportConfigWithEffectiveShowExcludedTxns = (config: ReportConfiguration): ReportConfiguration =>
  isExcludedInReportsFilterApplied(config)
    ? config.set('displayOptions', {
        hideCents: false,
        showExpensesAsNegative: false,
        ...config.displayOptions,
        showExcludedTxns: getEffectiveShowExcludedTxns(config),
      })
    : config;

export const isStartAtZeroReport = (reportSubType: ReportSubType | undefined): boolean =>
  reportSubType === 'NET_WORTH' || reportSubType === 'SAVINGS';

export const normalizeStartAtZeroDisplayOptions = (
  reportSubType: ReportSubType | undefined,
  displayOptions: DisplayOptionsType | undefined,
): DisplayOptionsType | undefined =>
  isStartAtZeroReport(reportSubType) && displayOptions
    ? { ...displayOptions, startAtZero: displayOptions.startAtZero ?? false }
    : displayOptions;

export const isStartAtZeroOptionEnabled = (
  reportSubType: ReportSubType | undefined,
  chartType: ChartType | undefined,
): boolean => isStartAtZeroReport(reportSubType) && chartType !== 'AREA';

/**
 * Report SubType Defaults & Validation
 */
export const getDefaultReportSubtype = (reportType: ReportType): ReportSubType => {
  switch (reportType) {
    case 'SUMMARY':
      return 'NET_INCOME';
    case 'TRANSACTION':
      return 'TAX_SCHEDULE';
    case 'COMPARISON':
      return 'NET_INCOME';
  }
};

/**
 * The subtype set enforced at runtime by `adjustReportSubtypeIfInvalid`, which runs on every
 * config write (reducer and saga). This is the authoritative list — `reportTypeRules.allowedSubtypes`
 * is only read by `getReportFieldConfig` and by `selectIsValidReportConfig`.
 *
 * The two are intentionally NOT derived from one another yet: they disagree for TRANSACTION, where
 * `allowedSubtypes` omits NET_WORTH and SAVINGS even though configs with those subtypes are
 * reachable and are left alone here. Unifying them would change `getReportFieldConfig`'s resolved
 * subtype for those existing reports, which is out of scope for the comparison work — see
 * SIMPL-34992. COMPARISON is kept consistent across both, and a test asserts that.
 */
const validReportSubtypesByType: Record<ReportType, Set<ReportSubType>> = {
  SUMMARY: new Set([
    'TAX_SUMMARY',
    'NET_INCOME',
    'CUSTOM',
    'NET_WORTH',
    'BALANCE_SHEET',
    'SAVINGS',
    'INCOME',
    'SPENDING',
  ] as const),
  TRANSACTION: new Set(['TAX_SCHEDULE', 'TAX_SUMMARY', 'INCOME', 'SPENDING', 'NET_WORTH', 'SAVINGS'] as const),
  // Keep in sync with `reportTypeRules.COMPARISON.allowedSubtypes` above — see the note below.
  COMPARISON: new Set(['INCOME', 'NET_INCOME', 'SPENDING', 'CUSTOM'] as const),
};

export const adjustReportSubtypeIfInvalid = (config: ReportConfiguration): ReportConfiguration => {
  const reportSubType = config.reportSubType;
  const reportType = config.reportType;

  const isReportSubtypeValid = validReportSubtypesByType[reportType]?.has(reportSubType);
  let adjustedConfig = config;

  if (!isReportSubtypeValid) {
    const defaultSubtype = getDefaultReportSubtype(reportType);
    adjustedConfig = adjustedConfig.merge({ reportSubType: defaultSubtype });
  }

  return adjustedConfig;
};

/**
 * Report Group By Adjustments (reuses existing logic from getGroupByOptions)
 */
const getDefaultGroupByForAdjustments = (reportSubType: ReportSubType): RowType => {
  switch (reportSubType) {
    case 'BALANCE_SHEET':
      return 'BUSINESS';
    case 'BALANCES_OVER_TIME':
      return 'NONE';
    case 'CUSTOM':
      return 'CATEGORY';
    case 'INCOME':
      return 'CATEGORY';
    case 'NET_INCOME':
      return 'CATEGORY';
    case 'NET_WORTH':
      return 'ACCOUNT';
    case 'PERFORMANCE_OVER_TIME':
      return 'NONE';
    case 'SAVINGS':
      return 'ACCOUNT';
    case 'SPENDING':
      return 'CATEGORY';
    case 'TAX_SCHEDULE':
      return 'USAGE';
    case 'TAX_SUMMARY':
      return 'USAGE';
    default:
      return 'NONE';
  }
};

const validGroupByBySubtypeForAdjustments: Record<ReportSubType, Set<RowType>> = {
  BALANCE_SHEET: new Set(['BUSINESS'] as const),
  BALANCES_OVER_TIME: new Set([]),
  CUSTOM: new Set([]),
  INCOME: new Set(['ACCOUNT', 'CATEGORY', 'PAYEE', 'TAG', 'NONE'] as const),
  NET_INCOME: new Set(['ACCOUNT', 'CATEGORY', 'PAYEE', 'TAG'] as const),
  NET_WORTH: new Set([]),
  PERFORMANCE_OVER_TIME: new Set([]),
  SAVINGS: new Set([]),
  SPENDING: new Set(['ACCOUNT', 'CATEGORY', 'PAYEE', 'TAG'] as const),
  TAX_SCHEDULE: new Set([]),
  TAX_SUMMARY: new Set(['BUSINESS', 'TAX_LINE_ITEM', 'USAGE'] as const),
  NONE: new Set([]),
};

export const adjustGroupByIfInvalid = (config: ReportConfiguration): ReportConfiguration => {
  const reportSubType = config.reportSubType;
  const reportType = config.reportType;
  const rowType = config.rowType;

  const isReportGroupByValid = validGroupByBySubtypeForAdjustments[reportSubType]?.has(rowType);
  let adjustedConfig = config;

  if (!isReportGroupByValid) {
    const defaultGroupBy = getDefaultGroupByForAdjustments(reportSubType);
    adjustedConfig = adjustedConfig.merge({ rowType: defaultGroupBy });
  }

  // Special handling for TAX_SUMMARY (matches NewReports logic)
  if (reportSubType === 'TAX_SUMMARY') {
    // Tax reports come in 2 flavors: either Detail (reportType === 'TRANSACTION') or Schedule (reportType === 'SUMMARY')
    const groupBy = reportType === 'TRANSACTION' ? 'USAGE' : 'BUSINESS';
    const segmentBy = reportType === 'TRANSACTION' ? 'NONE' : 'TIME';
    const timestep = reportType === 'TRANSACTION' ? 'NONE' : 'YEAR';

    adjustedConfig = adjustedConfig.merge({
      rowType: groupBy,
      columnType: segmentBy,
      timestep,
    });
  }

  return adjustedConfig;
};

/**
 * Report Segment By Validation
 */
export const adjustSegmentByIfInvalid = (config: ReportConfiguration): ReportConfiguration => {
  const reportType = config.reportType;
  const rowType = config.rowType;
  const columnType = config.columnType;

  // Comparisons support time columns only, including when reopening a saved configuration.
  // The interval is independent: NONE remains valid for a comparison of range totals.
  if (reportType === 'COMPARISON') {
    return columnType === 'TIME' ? config : config.merge({ columnType: 'TIME' });
  }

  let isSegmentByValid = true;

  if (reportType === 'TRANSACTION' && columnType !== 'NONE') {
    isSegmentByValid = false;
  } else if (rowType === columnType) {
    isSegmentByValid = false;
  }

  if (!isSegmentByValid) {
    // For TRANSACTION reports (including SPENDING subtype), set to NONE instead of TIME
    if (reportType === 'TRANSACTION') {
      return config.merge({ columnType: 'NONE' });
    }
    return config.merge({ columnType: 'TIME' });
  }

  return config;
};

/**
 * Filter Adjustments (Usage)
 */
export const adjustFilter = (config: ReportConfiguration, usageIds: string[]): ReportConfiguration => {
  const reportSubType = config.reportSubType;

  // Create or update the usage filter item
  const existingFilterItems = config.filter?.filterItems || [];
  const nonUsageFilterItems = existingFilterItems.filter((item) => item.type !== 'USAGE');

  const usageFilterItem: UsageFilterItemProps = {
    type: 'USAGE',
    usages: usageIds.map((id) => ({
      id: id === 'PERSONAL' ? '0' : id,
      type: id === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS',
    })),
  };

  const updatedFilterItems = [...nonUsageFilterItems, usageFilterItem];

  const updatedFilter = mkReportConfigFilters(
    config.filter ? { ...config.filter, filterItems: updatedFilterItems } : { filterItems: updatedFilterItems },
  );

  let adjustedConfig = config.merge({ filter: updatedFilter });

  // For any tax report (TAX_SUMMARY or TAX_SCHEDULE), if personal usage is selected,
  // convert to tax transaction report with columnType NONE and rowType USAGE
  if (reportSubType === 'TAX_SUMMARY' || reportSubType === 'TAX_SCHEDULE') {
    if (usageIds.includes('0') || usageIds.includes('PERSONAL')) {
      adjustedConfig = adjustedConfig.merge({
        reportType: 'TRANSACTION',
        reportSubType: 'TAX_SUMMARY',
        rowType: 'USAGE',
        columnType: 'NONE',
        timestep: 'NONE',
      });
    }
  }

  return adjustedConfig;
};
const maxDaysInMonth = 31;
export function determineTimestep(
  value:
    | {
        t: PREDEFINED_RANGE_VALUE | Exclude<GeneratedReportRanges, 'CUSTOM'>;
      }
    | {
        t: CUSTOM_RANGE_VALUE;
        custom: { start: string; end: string };
      }
    | undefined,
  today: DateTime,
): 'DAY' | 'MONTH' {
  const type = value?.t;
  switch (type) {
    case '60_DAYS':
    case '90_DAYS':
    case '365_DAYS':
    case 'NEXT_365_DAYS':
    case 'RECENT_2_MONTHS':
    case 'RECENT_3_MONTHS':
    case 'RECENT_6_MONTHS':
    case 'RECENT_12_MONTHS':
    case 'LAST_YEAR':
    case 'NEXT_60_DAYS':
    case 'THIS_QUARTER':
    case 'LAST_QUARTER':
    case 'NEXT_QUARTER':
    case '6_MONTHS':
    case '12_MONTHS':
    case '3_MONTHS_PRIOR':
    case '6_MONTHS_PRIOR':
    case '12_MONTHS_PRIOR':
    case undefined:
      return 'MONTH';
    case 'LAST_MONTH':
    case 'TODAY':
    case 'MONTH_TO_DATE':
    case 'THIS_MONTH':
    case 'NEXT_14_DAYS':
    case 'NEXT_MONTH':
    case 'NEXT_30_DAYS':
    case 'RECENT_30_DAYS':
    case '30_DAYS':
      return 'DAY';
    case 'YEAR_TO_DATE':
    case 'QUARTER_TO_DATE': {
      return type
        ? constructPredefinedInterval({ value: type }, today).count('day') <= maxDaysInMonth
          ? 'DAY'
          : 'MONTH'
        : 'MONTH';
    }
    case 'CUSTOM': {
      if (type === 'CUSTOM' && value && 'custom' in value) {
        return value?.custom
          ? DateTime.fromISO(value.custom.end).diff(DateTime.fromISO(value.custom.start), 'day').days <= maxDaysInMonth
            ? 'DAY'
            : 'MONTH'
          : 'MONTH';
      }
      return 'MONTH';
    }
    default:
      return 'MONTH';
  }
}

type DetermineTimestepInput = Parameters<typeof determineTimestep>[0];

/**
 * Build the `determineTimestep` input for a config's date range.
 *
 * The dynamic ranges (CUSTOM_SINCE / CUSTOM_LAST / CUSTOM_RECENT) don't map to a fixed timestep, so we
 * resolve them to concrete start/end dates and let the same span-based rule used for CUSTOM decide
 * (<= 31 days -> DAY, else MONTH).
 */
const buildDateRangeInputForTimestep = (config: ReportConfiguration, today: DateTime): DetermineTimestepInput => {
  const { dateRange } = config;

  if (dateRange === 'CUSTOM') {
    return { t: 'CUSTOM', custom: { start: config.startOn || '', end: config.endOn || '' } };
  }

  if (isGeneratedReportRangeTypeDynamic(dateRange)) {
    const dynamicRange: DynamicRange =
      dateRange === 'CUSTOM_SINCE'
        ? { type: 'CUSTOM_SINCE', startDate: DateTime.fromISO(config.startOn || '') }
        : {
            type: dateRange as 'CUSTOM_LAST' | 'CUSTOM_RECENT',
            interval: (config.dateRangeInterval as DynamicRangeIntervalTimestep) ?? 'MONTH',
            count: config.dateRangeIntervalCount ?? 1,
          };
    const interval = constructDynamicInterval(dynamicRange, today);
    return { t: 'CUSTOM', custom: { start: interval.start?.toISO() || '', end: interval.end?.toISO() || '' } };
  }

  return { t: dateRange };
};

/**
 * Clear date-bound and interval params that conflict with the config's dateRange.
 * QCS rejects requests where e.g. CUSTOM_SINCE carries a dateRangeInterval or a
 * predefined range carries startOn/endOn, and stale values can linger in stored
 * configs. Null (not undefined) is required — Immutable merge silently skips undefined.
 */
export type ConflictingDateRangeField = 'startOn' | 'endOn' | 'dateRangeInterval' | 'dateRangeIntervalCount';

export const getConflictingDateRangeFields = (dateRange: GeneratedReportRanges): ConflictingDateRangeField[] => {
  const fields: ConflictingDateRangeField[] = [];

  if (dateRange !== 'CUSTOM' && dateRange !== 'CUSTOM_SINCE') {
    fields.push('startOn');
  }
  if (dateRange !== 'CUSTOM') {
    fields.push('endOn');
  }
  if (dateRange !== 'CUSTOM_LAST' && dateRange !== 'CUSTOM_RECENT') {
    fields.push('dateRangeInterval', 'dateRangeIntervalCount');
  }

  return fields;
};

export const clearConflictingDateRangeFields = (config: ReportConfiguration): ReportConfiguration => {
  const overrides = getConflictingDateRangeFields(config.dateRange).reduce<
    Partial<Record<ConflictingDateRangeField, null>>
  >((fields, field) => ({ ...fields, [field]: null }), {});

  // @ts-expect-error The Record types do not include null, but mergeDeepEx requires an explicit
  // null to replace a stale value in client state rather than retaining it as it does for undefined.
  return config.merge(overrides);
};

/**
 * Omit fields that the selected dateRange does not permit before sending a configuration to QCS.
 * Client state uses null to clear stale values; the API contract requires these keys to be absent.
 */
export const omitConflictingDateRangeFields = (config: ReportConfigurationProps): ReportConfigurationProps => {
  const requestData = { ...config };
  for (const field of getConflictingDateRangeFields(config.dateRange)) {
    delete requestData[field];
  }
  return requestData;
};

/**
 * Date Range and Timestep Adjustments
 */
// when a timestep is default none, we dont want to switch it on data change or else reports will always show reset
export const adjustTimestepForDateRange = (
  config: ReportConfiguration,
  adjustProps?: { changedField: keyof ReportConfiguration; currentDate?: DateTime },
): ReportConfiguration => {
  const { reportSubType, reportType, timestep } = config;
  const isNetWorth = config.source === 'NET_WORTH'; // For net worth, we have specific timesteps and dateRange settings we do not want overridden.
  const shouldIgnoreChangedField =
    isNetWorth && (adjustProps?.changedField === 'dateRange' || adjustProps?.changedField === 'timestep');

  let nextTimestep: ReportConfigurationProps['timestep'] = timestep;

  if (reportType === 'TRANSACTION') {
    // Transaction reports never have a timestep, regardless of subtype or date range.
    nextTimestep = 'NONE'; // force unset
  } else if (reportSubType === 'TAX_SUMMARY') {
    // TAX_SUMMARY summary reports keep their existing timestep; don't recompute from date range.
  } else if (!shouldIgnoreChangedField && adjustProps?.changedField === 'dateRange' && timestep !== 'NONE') {
    const today = adjustProps.currentDate ?? DateTime.local();
    nextTimestep = determineTimestep(buildDateRangeInputForTimestep(config, today), today);
  } else if (!shouldIgnoreChangedField && adjustProps?.changedField === 'timestep' && timestep && timestep !== 'NONE') {
    nextTimestep = timestep ?? 'MONTH';
  }
  return clearConflictingDateRangeFields(config).merge({ timestep: nextTimestep });
};

/**
 * Comparison ranges only belong on COMPARISON reports, and each range's date sub-fields must match
 * its own `dateRange` mode — the contract rejects irrelevant date fields rather than ignoring them,
 * so hiding the inputs in the UI is not enough.
 *
 * Always writes `[]` rather than removing the key when leaving COMPARISON: `upsertResource` merges
 * through `mergeDeepEx`, which only replaces an existing array when the incoming value is truthy.
 * An `undefined` would leave the previous ranges in place.
 */
const adjustComparisonRanges = (config: ReportConfiguration): ReportConfiguration => {
  const { comparisonRanges, primaryAdditionalColumnTypes } = config;

  if (config.reportType !== 'COMPARISON') {
    const hasRanges = comparisonRanges && comparisonRanges.length > 0;
    const hasPrimaryColumns = primaryAdditionalColumnTypes && primaryAdditionalColumnTypes.length > 0;
    if (!hasRanges && !hasPrimaryColumns) {
      return config;
    }
    return config.merge({ comparisonRanges: [], primaryAdditionalColumnTypes: [] });
  }

  if (!comparisonRanges || comparisonRanges.length === 0) {
    return config;
  }

  const cleared = clearConflictingRangeDateFieldsAll(comparisonRanges);
  const isUnchanged = cleared.every((range, index) => _isEqual(range, comparisonRanges[index]));

  return isUnchanged ? config : config.merge({ comparisonRanges: cleared });
};

/**
 * Mileage deduction is only needed on tax reports
 */
const adjustDeductionType = (config: ReportConfiguration): ReportConfiguration => {
  const reportSubType = config.reportSubType;
  if (reportSubType === 'TAX_SUMMARY') {
    return config;
  }

  return config.remove('deductionType');
};

/**
 * Apply all relevant adjustments to a report configuration
 * This is the main function that orchestrates all adjustment logic
 */
export const adjustReportConfiguration = (
  config: ReportConfiguration,
  adjustProps?: AdjustedmentProps,
): ReportConfiguration => {
  let adjustedConfig = config;

  // Handle reportType changes with proper columnType and timestep adjustments
  if (adjustProps?.changedField === 'reportType') {
    adjustedConfig = adjustReportTypeIfInvalid(adjustedConfig, adjustedConfig.reportType);
    // After switching to SUMMARY, resolve the correct timestep for the current dateRange
    // (adjustTimestepForDateRange is skipped below when changedField is 'reportType')
    if (adjustedConfig.reportType === 'SUMMARY') {
      adjustedConfig = adjustTimestepForDateRange(adjustedConfig, {
        changedField: 'dateRange',
        currentDate: adjustProps.currentDate,
      });
    } else if (adjustedConfig.reportType === 'COMPARISON') {
      // Entering COMPARISON. `adjustReportTypeIfInvalid` above is called with the config's own
      // reportType, so by the time we get here from === to and its transition arms cannot fire, and
      // the source type's `chartType` (PIE / BAR_STACKED) would otherwise carry straight over onto
      // a report that renders no chart.
      //
      // Timestep seeds to NONE, which produces a single Period 1 group covering each range in full.
      // An earlier version clamped to MONTH on the assumption that NONE would leave no Period groups
      // for the ranges to align across; the deployed service disproves that — NONE returns exactly
      // one group, as the spec says it should. NONE is the better starting point while the derived
      // columns and Total group are still missing server-side, since a multi-period grid is mostly
      // empty without them. Revisit when SIMPL-35032 findings 4-7 land.
      //
      // Gated on `changedField === 'reportType'`, so this is a starting point only — the user can
      // pick any timestep afterwards and it will not be overridden.
      //
      // A comparison report with no ranges is invalid, so seed one rather than handing the user a
      // broken config. Only on entry: removing the last range while already in COMPARISON is the
      // user's business, and re-seeding there would make the range impossible to delete.
      const hasRanges = adjustedConfig.comparisonRanges && adjustedConfig.comparisonRanges.length > 0;

      adjustedConfig = adjustedConfig.merge({
        timestep: 'NONE',
        chartType: comparisonEntryChartType(adjustedConfig.reportSubType),
        ...(hasRanges ? {} : { comparisonRanges: [mkDefaultComparisonRange()] }),
      });
    }
  }

  // Check for personal usage in tax reports and apply necessary adjustments
  const usage = extractUsageFromFilter(adjustedConfig);
  const hasPersonalUsage = usage && usage.includes('0');
  const isTaxReport = adjustedConfig.reportSubType === 'TAX_SUMMARY' || adjustedConfig.reportSubType === 'TAX_SCHEDULE';

  if (hasPersonalUsage && isTaxReport) {
    adjustedConfig = adjustedConfig.merge({
      reportType: 'TRANSACTION',
      reportSubType: 'TAX_SUMMARY',
      rowType: 'USAGE',
      columnType: 'NONE',
      timestep: 'NONE',
    });
  }

  // Apply adjustments in the correct order (some depend on others)
  adjustedConfig = adjustReportSubtypeIfInvalid(adjustedConfig);
  adjustedConfig = adjustGroupByIfInvalid(adjustedConfig);
  adjustedConfig = adjustSegmentByIfInvalid(adjustedConfig);

  // Apply timestep adjustments BEFORE reportType adjustments to avoid overriding
  if (adjustProps?.changedField !== 'reportType') {
    adjustedConfig = adjustTimestepForDateRange(adjustedConfig, adjustProps);
  }

  if (adjustedConfig.reportType === 'SUMMARY') {
    if (adjustedConfig.columnType !== 'TIME') {
      adjustedConfig = adjustedConfig.merge({ chartType: 'NONE' });
    } else {
      const [overTimeChartTypes] = CHART_TYPE_OPTIONS[adjustedConfig.reportSubType];
      const allowedOverTimeCharts = (overTimeChartTypes ?? []) as ChartType[];
      const defaultChartType: ChartType = allowedOverTimeCharts[0] ?? 'NONE';
      const isValidChartType = allowedOverTimeCharts.includes((adjustedConfig.chartType ?? '') as ChartType);
      const nextChartType: ChartType =
        adjustedConfig.chartType && adjustedConfig.chartType !== 'NONE' && isValidChartType
          ? (adjustedConfig.chartType as ChartType)
          : defaultChartType;
      const nextTimestep =
        adjustedConfig.timestep && adjustedConfig.timestep !== 'NONE' ? adjustedConfig.timestep : 'MONTH';

      adjustedConfig = adjustedConfig.merge({
        chartType: nextChartType,
        timestep: nextTimestep,
      });
    }
  }

  // Heal the chart type against the subtype's comparison list, mirroring the SUMMARY block above.
  // This runs on every adjustment rather than only on entry, so it is what repairs a report *saved*
  // while comparison reports were grid-only and pinned to `chartType: 'NONE'` — without it those
  // configs would stay chart-less forever, since nothing else revisits the field.
  if (adjustedConfig.reportType === 'COMPARISON') {
    const allowedCharts = getComparisonChartTypeOptions(adjustedConfig.reportSubType, adjustedConfig.timestep);
    const isValidChartType = allowedCharts.includes((adjustedConfig.chartType ?? '') as ChartType);
    if (!isValidChartType) {
      adjustedConfig = adjustedConfig.merge({ chartType: allowedCharts[0] ?? 'NONE' });
    }
  }

  adjustedConfig = adjustDeductionType(adjustedConfig);
  adjustedConfig = adjustComparisonRanges(adjustedConfig);

  return adjustedConfig;
};

/**
 * Convenience function for applying adjustments when updating report type
 */
export const adjustReportConfigurationForReportType = (
  config: ReportConfiguration,
  reportType: ReportType,
): ReportConfiguration => {
  // Entering COMPARISON has to go through the `changedField: 'reportType'` arm of
  // `adjustReportConfiguration`: that arm seeds the first comparison range and clears a chartType
  // the comparison view cannot render. Without it, `adjustComparisonRanges` returns early on the
  // empty list and the result is a COMPARISON config with no ranges, which
  // `selectIsValidReportConfig` rejects.
  //
  // Deliberately not forwarded for the other target types, even though reportType is what changed
  // in every case. That arm also replaces `adjustTimestepForDateRange` with a recompute from the
  // date range, so passing it unconditionally would change TRANSACTION -> SUMMARY from timestep
  // MONTH to DAY — a pre-existing divergence between this helper and the main dispatch path that
  // is worth reconciling on its own ticket, not here.
  const enteringComparison = reportType === 'COMPARISON' && config.reportType !== 'COMPARISON';
  const adjustedConfig = adjustReportTypeIfInvalid(config, reportType);
  return adjustReportConfiguration(adjustedConfig, enteringComparison ? { changedField: 'reportType' } : undefined);
};
